import { z } from "zod"
import type { UIJSONSchema7 } from "../types"
import { type ErrorMessageConfig, defaultEnglishMessages, resolveMessage } from "./error-messages"
import { parseOneOfConditions } from "./schema-parser"

export interface ConvertToZodSchemaOptions {
	/**
	 * 필드별 Zod 스키마 오버라이드
	 * JSON Schema의 기본 변환을 무시하고 커스텀 Zod 스키마 적용
	 */
	fieldOverrides?: Record<string, z.ZodTypeAny>
	/**
	 * 에러 메시지 설정
	 * 기본값: 영어 메시지 (defaultEnglishMessages)
	 */
	errorMessages?: ErrorMessageConfig
}

/**
 * oneOf에서 discriminator 패턴 감지
 * @returns discriminator 필드명과 각 조건의 값/속성 오버라이드
 */
function parseOneOfDiscriminator(schema: UIJSONSchema7): {
	discriminatorField: string
	variants: Array<{ value: string; propertyOverrides: Record<string, UIJSONSchema7> }>
} | null {
	if (!schema.oneOf || !Array.isArray(schema.oneOf)) return null

	let discriminatorField: string | null = null
	const variants: Array<{ value: string; propertyOverrides: Record<string, UIJSONSchema7> }> = []

	for (const condition of schema.oneOf) {
		if (typeof condition === "boolean" || !condition.properties) continue

		// discriminator 필드 찾기 (const 값을 가진 property)
		let foundDiscriminator: string | null = null
		let discriminatorValue: string | null = null
		const propertyOverrides: Record<string, UIJSONSchema7> = {}

		for (const [propName, propSchema] of Object.entries(condition.properties)) {
			if (typeof propSchema === "boolean") continue

			if (propSchema.const !== undefined) {
				foundDiscriminator = propName
				discriminatorValue = String(propSchema.const)
			} else {
				// const가 아닌 속성은 오버라이드로 저장
				propertyOverrides[propName] = propSchema as UIJSONSchema7
			}
		}

		if (!foundDiscriminator || !discriminatorValue) continue

		// 첫 번째 discriminator 필드 확인
		if (discriminatorField === null) {
			discriminatorField = foundDiscriminator
		} else if (discriminatorField !== foundDiscriminator) {
			// 다른 discriminator 필드가 발견되면 패턴 불일치
			return null
		}

		variants.push({ value: discriminatorValue, propertyOverrides })
	}

	if (!discriminatorField || variants.length === 0) return null

	return { discriminatorField, variants }
}

/**
 * oneOf에서 enum 기반 패턴 감지 (schema-parser의 parseOneOfConditions 재사용)
 *
 * @returns 각 조건의 프로퍼티 오버라이드 목록 (enum 기반 discriminator가 감지된 경우)
 */
function parseOneOfEnumPattern(schema: UIJSONSchema7): {
	variants: Array<{ propertyOverrides: Record<string, UIJSONSchema7> }>
} | null {
	const oneOfConditions = parseOneOfConditions(schema)
	if (!oneOfConditions.isEnumBased || !oneOfConditions.discriminatorField) return null
	if (!schema.oneOf || !Array.isArray(schema.oneOf)) return null

	const conditions = schema.oneOf.filter((c): c is UIJSONSchema7 => typeof c !== "boolean" && !!c.properties)

	const variants = conditions.map((condition) => {
		const propertyOverrides: Record<string, UIJSONSchema7> = {}
		for (const [fieldName, fieldSchema] of Object.entries(condition.properties!)) {
			if (typeof fieldSchema === "boolean") continue
			propertyOverrides[fieldName] = fieldSchema as UIJSONSchema7
		}
		return { propertyOverrides }
	})

	return { variants }
}

/**
 * 필드 스키마에 오버라이드 적용 (minItems, maxItems 등)
 */
function mergeFieldSchema(base: UIJSONSchema7, override: UIJSONSchema7): UIJSONSchema7 {
	return { ...base, ...override }
}

/**
 * JSON Schema를 Zod Schema로 변환
 *
 * oneOf가 discriminator 패턴이면 자동으로 discriminatedUnion으로 변환
 *
 * @param schema - JSON Schema
 * @param options - 변환 옵션 (필드 오버라이드, 에러 메시지 등)
 * @returns Zod Schema
 */
export function convertToZodSchema(schema: UIJSONSchema7, options?: ConvertToZodSchemaOptions): z.ZodTypeAny {
	if (schema.type !== "object" || !schema.properties) {
		throw new Error("Schema must be an object type with properties")
	}

	const fieldOverrides = options?.fieldOverrides || {}
	const messages = options?.errorMessages ?? defaultEnglishMessages

	// oneOf discriminator 패턴 확인
	const discriminatorInfo = parseOneOfDiscriminator(schema)

	if (discriminatorInfo) {
		// discriminatedUnion으로 변환
		const { discriminatorField, variants } = discriminatorInfo
		const baseRequired = schema.required || []

		const variantSchemas = variants.map(({ value, propertyOverrides }) => {
			const shape: Record<string, z.ZodTypeAny> = {}

			// 모든 base properties 처리
			for (const [name, fieldSchema] of Object.entries(schema.properties!)) {
				if (name === discriminatorField) {
					// discriminator 필드는 literal로
					shape[name] = z.literal(value)
					continue
				}

				// 오버라이드가 있으면 병합
				const mergedSchema = propertyOverrides[name] ? mergeFieldSchema(fieldSchema, propertyOverrides[name]) : fieldSchema

				// fieldOverrides 우선 적용
				let fieldZod = fieldOverrides[name] ?? convertFieldToZod(mergedSchema, messages)

				if (!baseRequired.includes(name)) {
					fieldZod = fieldZod.optional()
				}

				shape[name] = fieldZod
			}

			return z.object(shape)
		})

		// discriminatedUnion은 최소 2개의 variant 필요
		if (variantSchemas.length >= 2) {
			return z.discriminatedUnion(discriminatorField, variantSchemas as unknown as [z.ZodObject<z.ZodRawShape>, z.ZodObject<z.ZodRawShape>, ...z.ZodObject<z.ZodRawShape>[]])
		}
	}

	// enum 기반 oneOf 패턴 확인 (const 없이 enum으로만 구성된 경우)
	const enumOneOfInfo = parseOneOfEnumPattern(schema)

	if (enumOneOfInfo) {
		const { variants } = enumOneOfInfo
		const baseRequired = schema.required || []

		const variantSchemas = variants.map(({ propertyOverrides }) => {
			const shape: Record<string, z.ZodTypeAny> = {}

			for (const [name, fieldSchema] of Object.entries(schema.properties!)) {
				// 오버라이드가 있으면 병합 (enum 제한 등)
				const mergedSchema = propertyOverrides[name] ? mergeFieldSchema(fieldSchema, propertyOverrides[name]) : fieldSchema

				let fieldZod = fieldOverrides[name] ?? convertFieldToZod(mergedSchema, messages)

				if (!baseRequired.includes(name)) {
					fieldZod = fieldZod.optional()
				}

				shape[name] = fieldZod
			}

			return z.object(shape)
		})

		if (variantSchemas.length >= 2) {
			return z.union(variantSchemas as unknown as [z.ZodObject<z.ZodRawShape>, z.ZodObject<z.ZodRawShape>, ...z.ZodObject<z.ZodRawShape>[]])
		}
	}

	// 일반 object 스키마 변환
	const shape: Record<string, z.ZodTypeAny> = {}
	const requiredFields = schema.required || []

	for (const [name, fieldSchema] of Object.entries(schema.properties)) {
		// 오버라이드가 있으면 사용, 없으면 기본 변환
		let fieldZod = fieldOverrides[name] ?? convertFieldToZod(fieldSchema, messages)

		if (!requiredFields.includes(name)) {
			fieldZod = fieldZod.optional()
		}

		shape[name] = fieldZod
	}

	return z.object(shape)
}

function convertFieldToZod(schema: UIJSONSchema7, messages: ErrorMessageConfig): z.ZodTypeAny {
	if (schema.enum) {
		const enumValues = schema.enum as [string | number, ...Array<string | number>]

		// Check if enum contains numbers
		if (typeof enumValues[0] === "number") {
			// Use z.union with z.literal for number enums
			const numberEnums = enumValues as number[]
			if (numberEnums.length === 1) {
				return z.literal(numberEnums[0])
			}
			const literals = numberEnums.map((v) => z.literal(v)) as [z.ZodLiteral<number>, z.ZodLiteral<number>, ...z.ZodLiteral<number>[]]
			return z.union(literals)
		}

		// Use z.enum for string enums
		return z.enum(enumValues as [string, ...string[]])
	}

	const requiredMsg = messages.required ?? "This field is required"

	switch (schema.type) {
		case "string": {
			let zodString = z.string({ error: requiredMsg })

			if (schema.minLength) {
				const msg = resolveMessage(messages.string?.min, schema.minLength, `Minimum ${schema.minLength} characters`)
				zodString = zodString.min(schema.minLength, msg)
			}

			if (schema.maxLength) {
				const msg = resolveMessage(messages.string?.max, schema.maxLength, `Maximum ${schema.maxLength} characters`)
				zodString = zodString.max(schema.maxLength, msg)
			}

			if (schema.pattern) {
				const msg = messages.string?.pattern ?? "Invalid format"
				zodString = zodString.regex(new RegExp(schema.pattern), msg)
			}

			return zodString
		}

		case "integer": {
			const intMsg = messages.number?.int ?? "Must be an integer"
			let zodNumber = z.number({ error: requiredMsg }).int(intMsg)

			if (schema.minimum !== undefined) {
				const msg = resolveMessage(messages.number?.min, schema.minimum, `Minimum ${schema.minimum}`)
				zodNumber = zodNumber.min(schema.minimum, msg)
			}

			if (schema.maximum !== undefined) {
				const msg = resolveMessage(messages.number?.max, schema.maximum, `Maximum ${schema.maximum}`)
				zodNumber = zodNumber.max(schema.maximum, msg)
			}

			return zodNumber
		}

		case "number": {
			let zodNumber = z.number({ error: requiredMsg })

			if (schema.minimum !== undefined) {
				const msg = resolveMessage(messages.number?.min, schema.minimum, `Minimum ${schema.minimum}`)
				zodNumber = zodNumber.min(schema.minimum, msg)
			}

			if (schema.maximum !== undefined) {
				const msg = resolveMessage(messages.number?.max, schema.maximum, `Maximum ${schema.maximum}`)
				zodNumber = zodNumber.max(schema.maximum, msg)
			}

			return zodNumber
		}

		case "boolean":
			return z.boolean({ error: requiredMsg })

		case "object":
			if (schema.properties) {
				return convertToZodSchema(schema)
			}
			return z.record(z.string(), z.unknown())

		case "array": {
			const itemSchema = schema.items && !Array.isArray(schema.items) ? convertFieldToZod(schema.items, messages) : z.unknown()
			let zodArray = z.array(itemSchema, { error: requiredMsg })

			if (schema.minItems !== undefined) {
				const msg = resolveMessage(messages.array?.min, schema.minItems, `Minimum ${schema.minItems} items`)
				zodArray = zodArray.min(schema.minItems, msg)
			}

			if (schema.maxItems !== undefined) {
				const msg = resolveMessage(messages.array?.max, schema.maxItems, `Maximum ${schema.maxItems} items`)
				zodArray = zodArray.max(schema.maxItems, msg)
			}

			return zodArray
		}

		default:
			return z.unknown()
	}
}
